import { FC, useEffect, useState } from 'react';

import { Button, Col, Form, FormInstance, Input, Row } from 'antd';
import cs from 'classnames';
import AuthService from 'services/authService';

import CountdownTimer from 'components/CountdownTimer/CountdownTimer';
import StaticNotification from 'components/StaticNotification/StaticNotification';

import useErrorCatch from 'hooks/useErrorCatch';

import { SECTION_ROW_GUTTER } from 'constants/commons';

import { ReactComponent as ArrowRight } from 'assets/icons/arrowRight.svg';

import styles from '../AuthJobseeker.module.scss';

interface VerificationCodeProps {
  isLoading: boolean;
  onSendVerificationCode: () => void;
  onCancelClick: () => void;
  form: FormInstance;
}

const VerificationCode: FC<VerificationCodeProps> = ({
  isLoading,
  onSendVerificationCode,
  onCancelClick,
  form
}) => {
  const [isTimerActive, setIsTimerActive] = useState(false);

  const catchError = useErrorCatch();

  const { email } = form.getFieldsValue(['email']);

  const message = (
    <>
      <b>{email}</b> is already registered. A verification code has been sent to
      your email. Please enter the code below to continue.
    </>
  );

  const requestCode = async (): Promise<void> => {
    try {
      await AuthService.requestVerificationCode({ email });
      setIsTimerActive(true);
    } catch (error) {
      catchError({
        error,
        default: {
          message:
            "Can't send verification code, please check you filled email correctly or try later"
        }
      });
    }
  };

  useEffect(() => {
    if (email) {
      requestCode();
    }
  }, [email]);

  return (
    <>
      <StaticNotification text={message} />
      <Row gutter={SECTION_ROW_GUTTER} className={styles.row}>
        <Col span={24}>
          <Form.Item
            name="code"
            label="Verification code"
            rules={[
              {
                required: true,
                message: 'Enter verification code'
              }
            ]}
          >
            <Input placeholder="______" maxLength={6} />
          </Form.Item>
        </Col>
      </Row>

      <div className={styles.submitContainer}>
        <Button
          className={cs(styles.button, styles.cancelButton)}
          type="primary"
          onClick={onCancelClick}
          loading={isLoading}
        >
          Cancel
        </Button>

        <Button
          className={styles.button}
          type="default"
          onClick={requestCode}
          disabled={isTimerActive}
        >
          {isTimerActive ? (
            <CountdownTimer
              initialTime={120}
              isActive={isTimerActive}
              onComplete={() => setIsTimerActive(false)}
              renderContent={(counter) => `Resend code (${counter}s)`}
            />
          ) : (
            'Get code'
          )}
        </Button>

        <Button
          className={styles.button}
          type="primary"
          icon={<ArrowRight />}
          iconPosition="end"
          onClick={onSendVerificationCode}
          loading={isLoading}
        >
          Log In
        </Button>
      </div>
    </>
  );
};

export default VerificationCode;
